import type {
  ExprNode,
  ExprRangeNode,
  ExprTextNode,
  ExprValueNode,
  UserNode,
} from '@revelio/data-access';

/**
 * `UserNode` is the outer wrapper type in the AI v2 payload.
 *
 * Roughly:
 * - `user_value.child` contains the actual filter expression (`ExprNode`)
 * - `user_and` / `user_or` combine multiple `UserNode`s
 * - `user_not` negates a `UserNode`
 *
 * We first flatten that wrapper tree into "atoms" so the rest of the transform can
 * work purely with `ExprNode`s.
 */
export type UserValueAtom = {
  expr: ExprNode;
  isNegated: boolean;
};

export const flattenUserNodeToAtoms = (
  node: UserNode,
  isNegated = false
): UserValueAtom[] => {
  // `NOT` toggles the negation flag for whatever is nested below.
  if (node.user_not?.child) {
    return flattenUserNodeToAtoms(node.user_not.child, !isNegated);
  }

  // A `user_value` node is where the actual `ExprNode` lives.
  if (node.user_value?.child) {
    return [{ expr: node.user_value.child, isNegated }];
  }

  // `user_and` / `user_or` are just containers at this level.
  // We flatten them into a list of atoms because the CustomDash UI is filter-instance based.
  const children = node.user_and?.children ?? node.user_or?.children;
  if (!children || children.length === 0) {
    return [];
  }

  return children.flatMap((child) => flattenUserNodeToAtoms(child, isNegated));
};

export type ExprValLeaf = { val: ExprValueNode; isNegated: boolean };
export type ExprTxtLeaf = { txt: ExprTextNode; isNegated: boolean };
export type ExprRngLeaf = { rng: ExprRangeNode; isNegated: boolean };

/**
 * Splits an expression into top-level AND conjuncts (best-effort).
 *
 * - Only splits through `and` nodes.
 * - Does NOT split through `not`, because negated conjunctions (De Morgan) cannot be
 *   represented as multiple independent "chips" without changing semantics.
 *
 * Example:
 * - `A AND (B OR C) AND D` → [A, (B OR C), D]
 */
export const splitExprIntoTopLevelAndConjuncts = (
  node: ExprNode
): ExprNode[] => {
  if (node.not?.child) return [node];
  const children = node.and?.children;
  if (!children || children.length === 0) return [node];
  return children.flatMap(splitExprIntoTopLevelAndConjuncts);
};

export const collectValLeaves = (
  node: ExprNode,
  isNegated = false
): ExprValLeaf[] => {
  // We propagate negation by toggling the flag whenever we cross a `not` node.
  if (node.not?.child) {
    return collectValLeaves(node.not.child, !isNegated);
  }

  // A "leaf" here is the typed payload node we ultimately want to map into a CustomDash filter.
  if (node.val) {
    return [{ val: node.val, isNegated }];
  }

  if (node.or?.children?.length) {
    return node.or.children.flatMap((c) => collectValLeaves(c, isNegated));
  }

  if (node.and?.children?.length) {
    return node.and.children.flatMap((c) => collectValLeaves(c, isNegated));
  }

  return [];
};

export const collectTxtLeaves = (
  node: ExprNode,
  isNegated = false
): ExprTxtLeaf[] => {
  // Same traversal shape as `collectValLeaves`, but collecting `.txt` nodes instead.
  if (node.not?.child) {
    return collectTxtLeaves(node.not.child, !isNegated);
  }

  if (node.txt) {
    return [{ txt: node.txt, isNegated }];
  }

  if (node.or?.children?.length) {
    return node.or.children.flatMap((c) => collectTxtLeaves(c, isNegated));
  }

  if (node.and?.children?.length) {
    return node.and.children.flatMap((c) => collectTxtLeaves(c, isNegated));
  }

  return [];
};

export const collectRngLeaves = (
  node: ExprNode,
  isNegated = false
): ExprRngLeaf[] => {
  // Same traversal shape as `collectValLeaves`, but collecting `.rng` nodes instead.
  if (node.not?.child) {
    return collectRngLeaves(node.not.child, !isNegated);
  }

  if (node.rng) {
    return [{ rng: node.rng, isNegated }];
  }

  if (node.or?.children?.length) {
    return node.or.children.flatMap((c) => collectRngLeaves(c, isNegated));
  }

  if (node.and?.children?.length) {
    return node.and.children.flatMap((c) => collectRngLeaves(c, isNegated));
  }

  return [];
};
